import {
  Typography,
  Box,
  Button,
  AppBar,
  Autocomplete,
  createFilterOptions,
  TextField,
  outlinedInputClasses,
  autocompleteClasses,
  formHelperTextClasses,
  inputLabelClasses,
  chipClasses,
  paperClasses,
} from "@mui/material";
import { Fragment, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useLocation, useParams } from "react-router-dom";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import { Add, Clear } from "@mui/icons-material";
import TabPanel from "@mui/lab/TabPanel";
import {
  selectFilterRole,
  selectFilterRoleUpdateStatus,
  selectMembersUpdateStatus,
  selectSwitchMemberStatus,
} from "store/filterRole/selectors";
import {
  addMembersToFilterRoleByIDs,
  getFilterRoleByID,
} from "store/filterRole/thunks";
import { selectNonFilterRoleMembers, selectUsers } from "store/user/selectors";
import { RequestStatus as Status } from "constants/RequestStatus";
import { useAppSelector } from "utils/hooks/storeHooks";
import { FilterRole } from "types/FilterRole";
import { User } from "types/User";
import Modal from "components/Modal/Modal";
import MembersTable from "components/RolesTable/MembersTable";
import RoleForm from "../components/RoleForm";
import Toast from "components/Toast/Toast";
import { RoleToastMessages } from "constants/Role";
import variables from "../../../styles/partials/_variables.module.scss";
import styles from "./RoleDetails.module.scss";
import {
  setFilterRoleUpdateStatus,
  setMembersUpdateStatus,
  setSwitchMemberStatus,
} from "store/filterRole";
import { getAllUsers } from "store/user/thunks";

const RoleDetails = () => {
  const dispatch = useDispatch();
  const { roleId } = useParams();
  const { hash: urlHash, pathname } = useLocation();
  const isNewRole = pathname.includes("New");
  const [tab, setTab] = useState<string>(urlHash.replace("#", "") || "members");
  const [openModal, setOpenModal] = useState(false);
  const [addMembersArray, setAddMembersArray] = useState<User[]>([]);
  const [undoAddMemberId, setUndoAddMemberId] = useState<number | undefined>();
  const userIDsArray = addMembersArray.map((user: User) => user.id);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState({
    message: "",
    variant: "",
  });
  const allUsers: User[] = useAppSelector(selectUsers);
  const availableUsers: User[] = useAppSelector(selectNonFilterRoleMembers);
  const roleUpdateStatus = useAppSelector(selectFilterRoleUpdateStatus);
  const membersUpdateStatus = useAppSelector(selectMembersUpdateStatus);
  const switchMemberStatus = useAppSelector(selectSwitchMemberStatus);
  const existingRole = useAppSelector(selectFilterRole);
  const newRole: FilterRole = {
    name: "New Role",
    description: "",
    permission: [],
    isAdmin: false,
  };
  let role: FilterRole = isNewRole ? newRole : existingRole;

  useEffect(() => {
    if (!isNewRole) dispatch(getFilterRoleByID(Number(roleId)));
    if (isNewRole) dispatch(getAllUsers());
  }, [dispatch, roleId, isNewRole]);

  const handleChange = (event: React.SyntheticEvent, newTab: string) => {
    setTab(newTab);
  };

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setAddMembersArray([]);
  };

  const handleAddMembers = () => {
    setUndoAddMemberId(undefined);
    if (roleId) dispatch(addMembersToFilterRoleByIDs(roleId, userIDsArray));
    setTab("members");
    handleCloseModal();
  };

  // filters up to 25 rendered options displayed on the autocomplete list
  const filterOptions = createFilterOptions({
    matchFrom: "any",
    stringify: (option: User) => option.name + " " + option.email,
    limit: 25,
    ignoreCase: true,
    ignoreAccents: true,
    trim: true,
  });

  //  ===============================  LAUNCH SNACKBAR  ===============================

  useEffect(() => {
    if (roleUpdateStatus === Status.Success) {
      setToastMessage(RoleToastMessages.editRoleSuccess);
    }
    if (roleUpdateStatus === Status.Failed) {
      setToastMessage(RoleToastMessages.editRoleError);
    }
    if (membersUpdateStatus === Status.Success) {
      setUndoAddMemberId(undefined); // so that the undo button doesn't show in the tw¿oast
      setToastMessage(RoleToastMessages.addMemberSuccess);
    }
    if (membersUpdateStatus === Status.Failed) {
      setUndoAddMemberId(undefined); // so that the undo button doesn't show in the tw¿oast
      setToastMessage(RoleToastMessages.addMemberError);
    }
    if (switchMemberStatus === Status.Success) {
      setToastMessage(RoleToastMessages.switchMemberSuccess);
    }
    if (switchMemberStatus === Status.Failed) {
      setToastMessage(RoleToastMessages.switchMemberError);
    }
    if (toastMessage.message !== "") {
      setShowToast(true);
    }
  }, [
    membersUpdateStatus,
    roleUpdateStatus,
    switchMemberStatus,
    toastMessage.message,
  ]);

  const undoChangeRole = undoAddMemberId
    ? [Number(existingRole.id), Number(undoAddMemberId)]
    : [];

  return (
    <Fragment>
      <Toast
        key="roles-management"
        message={toastMessage.message}
        open={showToast}
        undoChanges={undoChangeRole}
        variant={toastMessage.variant}
        onClose={() => {
          dispatch(setFilterRoleUpdateStatus(Status.Idle));
          dispatch(setMembersUpdateStatus(Status.Idle));
          dispatch(setSwitchMemberStatus(Status.Idle));
          setShowToast(false);
        }}
      />
      <AppBar className={styles.appBar} position="static">
        <Box className={styles.header}>
          <Link to="/RolesManagement">
            <ArrowBackRoundedIcon
              color="primary"
              fontSize="large"
              sx={{ margin: "0 0.75rem" }}
            />
          </Link>
          <Typography variant="subtitle2">{role.name}</Typography>
        </Box>
      </AppBar>
      <Modal
        actionLabel={`Add new ${
          addMembersArray.length <= 1 ? "member" : "members"
        }`}
        height="25rem"
        modalContent={
          <Box mt="1rem">
            <Autocomplete
              autoComplete
              ChipProps={{
                deleteIcon: <Clear />,
                sx: {
                  backgroundColor: variables.backgroundGray,
                  color: variables.darkBlue,
                  height: "1.5rem",
                  [`&.${autocompleteClasses.tag}`]: {
                    borderRadius: "8px",
                    margin: "0 .5rem 0 0",
                  },
                  [`& .${chipClasses.deleteIcon}`]: {
                    color: variables.darkBlue,
                  },
                },
              }}
              componentsProps={{
                // shadow effect on the list
                paper: {
                  elevation: 3,
                  sx: {
                    [`&.${paperClasses.root}`]: { padding: 0, minWidth: 0 },
                  },
                },
              }}
              disableClearable
              filterOptions={filterOptions}
              filterSelectedOptions
              getOptionLabel={(option) => option.name ?? option.email}
              limitTags={3}
              multiple
              noOptionsText="No available users found."
              openOnFocus
              options={isNewRole ? allUsers : availableUsers}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Members"
                  InputProps={{
                    ...params.InputProps,
                    type: "text",
                    sx: {
                      fontFamily: "Rubik",
                      [`&.${outlinedInputClasses.root}`]: {
                        padding: ".75rem",
                        [`& .${autocompleteClasses.input}`]: {
                          height: "1.5rem",
                          padding: "0 4px 0 6px",
                        },
                        "& fieldset": {
                          borderColor: variables.paragraphBaseline,
                          borderRadius: "8px",
                        },
                      },
                      [`&.Mui-focused .${outlinedInputClasses.notchedOutline}`]:
                        {
                          borderColor: `${variables.darkBlue}!important`,
                          borderWidth: "2px",
                        },
                      [`.${formHelperTextClasses.root}`]: {
                        margin: "0.25rem 1rem 0 1rem",
                        fontWeight: 400,
                        fontSize: ".75rem",
                        lineHeight: "1rem",
                        letterSpacing: "0.008em",
                        color: variables.paragraphBaseline,
                        textAlign: "start",
                      },
                    },
                  }}
                />
              )}
              renderOption={(props, option) => (
                // Allows to edit the list's individual list options format rendering
                <li {...props}>
                  <Typography variant="avatarInitial">
                    {option.name[0]}
                  </Typography>
                  <Box
                    sx={{
                      color: variables.paragraphBaseline,
                      display: "flex",
                      alignItems: "center",
                      width: "90%",
                      height: "18px",
                      marginLeft: "0.8rem",
                      marginRight: "0.3rem",
                      wordBreak: "break-all",
                      overflow: "hidden",
                    }}
                  >
                    <Typography
                      sx={{
                        color: variables.paragraphBaseline,
                        minWidth: "max-content",
                      }}
                      variant="body1"
                    >
                      {" "}
                      {option.name} &nbsp; &nbsp;{" "}
                    </Typography>
                    <Typography color={variables.paragraphPlaceholder}>
                      |
                    </Typography>
                    <Typography
                      sx={{
                        color: variables.paragraphBaseline,
                        fontSize: "0.875rem",
                        fontWeight: 400,
                        lineHeight: "24px",
                        minWidth: "max-content",
                      }}
                    >
                      {" "}
                      &nbsp; &nbsp; {option.email}{" "}
                    </Typography>
                  </Box>
                  <Add sx={{ color: variables.darkBlue }} />
                </li>
              )}
              sx={{
                [`& .${inputLabelClasses.root}`]: {
                  fontFamily: "Rubik",
                  top: "-0.225rem",
                  "&.Mui-focused": {
                    color: variables.darkBlue,
                  },
                },
                width: "100%",
              }}
              value={addMembersArray}
              onChange={(_, value) => {
                setAddMembersArray(value);
              }}
            />
          </Box>
        }
        isActionBtnDisabled={addMembersArray.length === 0}
        open={openModal}
        padding="3.5rem"
        title="Add members"
        titleHelperText="Type a name or email to Add a Member."
        width="700px"
        onClose={handleCloseModal}
        onSubmit={handleAddMembers}
      />

      <Box className={styles.roleDetailsLayout}>
        <Box className={styles.mainLayout}>
          <Box className={styles.detailsTopSection}>
            <Box className={styles.headerContainer}>
              <Typography mb="0.75rem" variant="h2">
                {role.name}
              </Typography>
              <Typography color={variables.tableGray} variant="body1">
                {" "}
                Create, view and edit roles & their permissions according to
                your organization.
              </Typography>
            </Box>
            <Box className={styles.buttonContainer}>
              <Button
                disabled={isNewRole}
                fullWidth
                sx={{ minWidth: "150px", width: "209px" }}
                type="submit"
                variant="contained"
                onClick={handleOpenModal}
              >
                Add members
              </Button>
            </Box>
          </Box>
          <Box>
            <TabContext value={tab}>
              <TabList
                aria-label="lab API tabs example"
                sx={{ backgroundColor: variables.backgroundGray }}
                onChange={handleChange}
              >
                <Tab
                  label="Role Details"
                  value="details"
                  sx={{
                    backgroundColor: variables.backgroundWhite,
                    borderRadius: "0.75rem 0.75rem 0 0",
                    typography: "body1",
                    "&.Mui-selected": {
                      fontWeight: 500,
                    },
                  }}
                />
                <Tab
                  label="Members"
                  value="members"
                  sx={{
                    backgroundColor: variables.backgroundWhite,
                    borderRadius: "0.75rem 0.75rem 0 0",
                    typography: "body1",
                    "&.Mui-selected": {
                      fontWeight: 500,
                    },
                  }}
                />
              </TabList>
              <Box className={styles.detailsBottomSection}>
                <TabPanel value="details">
                  <RoleForm isNewRole={isNewRole} role={role} />
                </TabPanel>
                <TabPanel value="members">
                  <Typography
                    color="#3D3D3D"
                    fontSize="1.125rem"
                    variant="body2"
                  >
                    Members list
                  </Typography>
                  <Typography
                    color={variables.tableGray}
                    mb="1.5rem"
                    variant="body2"
                  >
                    Here you can find a list of members assigned to this role
                  </Typography>
                  {isNewRole ? ( // display an empty members table when it's a new role
                    <MembersTable currentRoleId={0} currentRoleName={""} />
                  ) : (
                    <MembersTable
                      changedMemberId={(id) => setUndoAddMemberId(id)}
                      currentRoleId={Number(existingRole.id)}
                      currentRoleName={existingRole.name}
                    />
                  )}
                </TabPanel>
              </Box>
            </TabContext>
          </Box>
        </Box>
      </Box>
    </Fragment>
  );
};

export default RoleDetails;
